import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/button';
import MagicButton from './ui/magic-button';
import { Play, ArrowRight } from 'lucide-react';

const Hero = () => {
  const heroRef = useRef(null);
  const videoRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleMouseMove = (e) => {
    const { clientX, clientY } = e;
    setMousePosition({ x: clientX, y: clientY });
  };

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const easeInOut = (t) => {
      return t < 0.5
        ? 2 * t * t
        : 1 - Math.pow(-2 * t + 2, 2) / 2;
    };

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const heroHeight = hero.offsetHeight;

      // Optimization: Only animate while hero is visible or easing out
      if (scrollTop > heroHeight) return;

      let progress = scrollTop / heroHeight;
      progress = Math.min(Math.max(progress, 0), 1);

      const eased = easeInOut(progress);

      // Move background position based on eased progress
      // Initial position 0% 50%, Moves to 100% 50%
      hero.style.backgroundPosition = `${eased * 100}% 50%`;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      onMouseMove={handleMouseMove}
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-scroll-gradient pt-20"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        {/* Grid Pattern with Spotlight */}
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(to right, #444 1px, transparent 1px),
              linear-gradient(to bottom, #444 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            maskImage: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, black, transparent)`,
            WebkitMaskImage: `radial-gradient(600px circle at ${mousePosition.x}px ${mousePosition.y}px, black, transparent)`
          }}
        />

        <motion.div
          className="absolute top-1/4 -left-20 w-96 h-96 bg-primary rounded-full opacity-10 blur-[120px]"
          animate={{
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
            y: [0, -30, 0]
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div
          className="absolute bottom-1/4 -right-20 w-96 h-96 bg-primary rounded-full opacity-10 blur-[120px]"
          animate={{
            scale: [1, 1.3, 1],
            x: [0, -50, 0],
            y: [0, 30, 0]
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </div>

      {/* Video background placeholder - space for future video */}
      <div className="absolute inset-0 z-0">
        <div className="w-full h-full" style={{ background: 'linear-gradient(rgba(11,11,11,0.85), rgba(11,11,11,0.95))' }} />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-8 pt-10 pb-16 lg:py-0">

        {/* Left Side: Content */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex-1 flex flex-col items-center lg:items-start text-center lg:text-left w-full"
        >
          <motion.button
            onClick={() => scrollToSection('services')}
            className="inline-flex items-center gap-2 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full px-6 py-3 mb-6 cursor-pointer hover:bg-white/10 transition-colors duration-300"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Play className="w-4 h-4 text-primary" fill="currentColor" />
            <span className="text-sm text-white/80 font-medium">Storytelling Video Editor</span>
          </motion.button>

          <div className="relative z-50 isolation-isolate pointer-events-none">
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold mb-6 leading-[1.1] tracking-tight overflow-visible pointer-events-auto"
              style={{ WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' }}
            >
              <span className="inline-block bg-gradient-to-r from-white via-gray-200 to-white bg-[length:200%_auto] bg-clip-text text-transparent transition-all duration-700 ease-in-out hover:bg-right hover:tracking-wide cursor-default hover:drop-shadow-[0_0_10px_rgba(255,255,255,0.3)]">
                Transforming Raw Footage
              </span>
              <br />
              <span className="relative z-50 inline-block bg-gradient-to-r from-primary via-yellow-200 to-primary bg-[length:200%_auto] bg-clip-text text-transparent transition-all duration-700 ease-in-out hover:bg-right hover:tracking-wide cursor-default py-2">
                Into Engaging Stories
              </span>
            </h1>
          </div>

          <p className="text-lg md:text-xl text-white/70 mb-10 max-w-2xl mx-auto lg:mx-0 leading-relaxed">
            Stop losing viewers to poor pacing. We turn raw footage into high-retention assets that grow your channel and brand.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-5 mb-10 w-full sm:w-auto">
            <MagicButton
              onClick={() => scrollToSection('featured-work')}
              className="w-full sm:w-auto min-w-[200px]"
            >
              View Client Work
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </MagicButton>

            <MagicButton
              onClick={() => scrollToSection('contact')}
              className="w-full sm:w-auto min-w-[200px]"
            >
              Get Your Video Edited
            </MagicButton>
          </div>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={{
              visible: { transition: { staggerChildren: 0.03, delayChildren: 0.5 } }
            }}
            className="text-white/40 text-sm font-medium tracking-wide"
          >
            {"Worked with 20+ creators across different niches".split("").map((char, index) => (
              <motion.span
                key={index}
                variants={{
                  hidden: { opacity: 0 },
                  visible: { opacity: 1 }
                }}
              >
                {char}
              </motion.span>
            ))}
          </motion.p>
        </motion.div>

        {/* Right Side: Video Card Container */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
          className="w-full max-w-[280px] sm:max-w-[320px] lg:max-w-[360px] relative lg:ml-auto shrink-0 group z-20 mt-10 lg:mt-0"
        >
          {/* Animated Ambient Glow Backdrop */}
          <div className="absolute -inset-2 bg-gradient-to-tr from-primary/30 via-primary/5 to-primary/40 rounded-[40px] blur-2xl opacity-60 group-hover:opacity-100 transition duration-700 animate-pulse"></div>

          {/* Premium Bezel / Glass Wrapper */}
          <div className="relative rounded-[32px] overflow-hidden bg-[#111] p-[2px] shadow-[0_30px_60px_-15px_rgba(255,200,0,0.2)] transform transition-transform duration-500 group-hover:scale-[1.02] border border-white/5">
            {/* Bezel Light Reflection */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/80 pointer-events-none rounded-[32px] z-10"></div>

            <div className="w-full aspect-[9/16] relative rounded-[30px] overflow-hidden bg-black shadow-[inset_0_0_20px_rgba(0,0,0,0.8)]">
              {/* Featured Work Badge */}
              <div className="absolute top-4 left-4 z-30 flex items-center gap-2 bg-black/60 backdrop-blur-md border border-white/10 rounded-full px-3 py-1.5 shadow-lg pointer-events-none">
                <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_rgba(255,200,0,0.8)]" />
                <span className="text-xs font-semibold tracking-wide text-white/95">Show Reel</span>
              </div>

              {/* Video Element */}
              <video
                ref={videoRef}
                src="https://res.cloudinary.com/yl5brp7y/video/upload/v1786198003/FOR_PORTFOLIO_didly3.mp4"
                controls
                playsInline
                preload="auto"
                autoPlay
                muted
                loop
                className="w-full h-full object-cover [&:fullscreen]:object-contain [&:-webkit-full-screen]:object-contain relative z-10"
              />
            </div>
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Hero;